/**
 * Spain / Thailand province list: delete-only rows, Natural Earth land-area focus chart, sort — mirrors CountyTargetingField for Kenya.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  GTG_TARGET_COUNTRY_ES,
  GTG_TARGET_COUNTRY_TH,
  normalizeGtgTargetCountry,
  getGtgCanonicalSubdivisionList,
  parseSubdivisionsForTargetCountry,
  resolveProvinceCanonical,
  gtgSubdivisionPluralNoun,
} from "@healthdesk/shared-utils";
import {
  getGtgProvinceDisplayName,
  getGtgProvinceTargetingSnapshot,
  GTG_PROVINCE_GEO_VINTAGE_CAPTION,
  GTG_PROVINCE_GOOGLE_ADS_NOTE,
  SPAIN_HEALTH_STATS_CAPTION,
  THAILAND_HEALTH_STATS_CAPTION,
} from "../../services/census/getGtgProvinceMapContext";

/** @typedef {'decimal' | 'integer' | 'percent'} GtgProvinceMetricFormat */

/** @type {{ key: string, label: string, format: GtgProvinceMetricFormat }[]} */
const FOCUS_METRICS_ES = [
  {
    key: "crudeDeathRatePer1000",
    label: "Crude death rate ‰ (Eurostat demo_r_gind3, 2022)",
    format: "decimal",
  },
  {
    key: "crudeBirthRatePer1000",
    label: "Crude birth rate ‰ (Eurostat demo_r_gind3, 2022)",
    format: "decimal",
  },
  {
    key: "totalPopulation",
    label: "Population on 1 January (Eurostat, 2022)",
    format: "integer",
  },
  {
    key: "landAreaSqKm",
    label: "Land area (km², Natural Earth admin-1, sphere approx.)",
    format: "decimal",
  },
];

/** @type {{ key: string, label: string, format: GtgProvinceMetricFormat }[]} */
const FOCUS_METRICS_TH = [
  {
    key: "totalPopulation",
    label: "Population (Thailand census 2010, NSO)",
    format: "integer",
  },
  {
    key: "pctFemalePopulation",
    label: "% female population (Thailand census 2010, NSO)",
    format: "percent",
  },
  {
    key: "landAreaSqKm",
    label: "Land area (km², Natural Earth admin-1, sphere approx.)",
    format: "decimal",
  },
];

/** @param {string} tc */
function focusMetricsForCountry(tc) {
  if (tc === GTG_TARGET_COUNTRY_ES) return FOCUS_METRICS_ES;
  if (tc === GTG_TARGET_COUNTRY_TH) return FOCUS_METRICS_TH;
  return FOCUS_METRICS_TH;
}

/** @param {string} tc */
function defaultMetricKeyForCountry(tc) {
  if (tc === GTG_TARGET_COUNTRY_ES) return "crudeDeathRatePer1000";
  if (tc === GTG_TARGET_COUNTRY_TH) return "totalPopulation";
  return "landAreaSqKm";
}

function formatMetricDisplay(n, format) {
  if (n == null || !Number.isFinite(n)) return "—";
  if (format === "decimal") return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (format === "percent") return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  return `${Math.round(n).toLocaleString()}`;
}

function metricNumeric(snap, metricKey) {
  if (!snap || typeof snap !== "object") return null;
  const v = snap[metricKey];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function parseOrderedProvinceLines(text, canonList) {
  const parts =
    typeof text === "string" && text.trim()
      ? text
          .split(/[\n,;]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  return parts.map((raw) => ({
    raw,
    canonical: resolveProvinceCanonical(raw, canonList),
  }));
}

function valueFromLines(lines) {
  return lines.map((l) => (l.canonical ? l.canonical : l.raw)).join("\n");
}

function sortLinesByMetric(lines, snapshots, metricKey, ascending) {
  const indexed = lines.map((line, idx) => ({ line, idx }));
  const known = indexed.filter((x) => x.line.canonical);
  const unknown = indexed.filter((x) => !x.line.canonical);
  known.sort((a, b) => {
    const na = metricNumeric(snapshots[a.line.canonical], metricKey);
    const nb = metricNumeric(snapshots[b.line.canonical], metricKey);
    const aNull = na == null;
    const bNull = nb == null;
    if (aNull && bNull) return a.idx - b.idx;
    if (aNull) return 1;
    if (bNull) return -1;
    if (na === nb) return a.idx - b.idx;
    return ascending ? na - nb : nb - na;
  });
  return [...known, ...unknown].map((x) => x.line);
}

/**
 * @param {{
 *   targetCountry: string,
 *   value: string,
 *   onChange: (next: string) => void,
 *   disabled?: boolean,
 *   label?: string,
 *   sx?: object,
 * }} props
 */
const GtgProvinceTargetingField = ({
  targetCountry,
  value,
  onChange,
  disabled = false,
  label: labelProp,
  sx = {},
}) => {
  const tc = normalizeGtgTargetCountry(targetCountry);
  const theme = useTheme();
  const canonList = useMemo(() => [...getGtgCanonicalSubdivisionList(tc)], [tc]);
  const plural = gtgSubdivisionPluralNoun(tc);
  const label =
    labelProp ||
    (tc === GTG_TARGET_COUNTRY_ES
      ? "Spain provinces in this group"
      : tc === GTG_TARGET_COUNTRY_TH
        ? "Thailand provinces in this group"
        : "Provinces in this group");

  const lines = useMemo(() => parseOrderedProvinceLines(value, canonList), [value, canonList]);
  const parsedSummary = useMemo(() => parseSubdivisionsForTargetCountry(value || "", tc), [value, tc]);

  const focusMetricList = useMemo(() => focusMetricsForCountry(tc), [tc]);
  const [focusMetric, setFocusMetric] = useState(() => defaultMetricKeyForCountry(tc));
  useEffect(() => {
    setFocusMetric(defaultMetricKeyForCountry(tc));
  }, [tc]);
  const metricMeta = useMemo(
    () => focusMetricList.find((m) => m.key === focusMetric) ?? focusMetricList[0],
    [focusMetricList, focusMetric]
  );

  const snapshots = useMemo(() => {
    /** @type {Record<string, Record<string, unknown>|null>} */
    const m = {};
    for (const p of canonList) {
      m[p] = getGtgProvinceTargetingSnapshot(tc, p);
    }
    return m;
  }, [canonList, tc]);

  const removeAt = useCallback(
    (idx) => {
      const next = lines.filter((_, i) => i !== idx);
      onChange(valueFromLines(next));
    },
    [lines, onChange]
  );

  const applySort = useCallback(
    (ascending) => {
      const sorted = sortLinesByMetric(lines, snapshots, focusMetric, ascending);
      onChange(valueFromLines(sorted));
    },
    [lines, snapshots, focusMetric, onChange]
  );

  const chartData = useMemo(() => {
    if (!metricMeta || lines.length === 0) return [];
    return lines.map((line, i) => {
      const name = line.canonical || line.raw;
      const snap = line.canonical ? snapshots[line.canonical] : null;
      const raw = metricNumeric(snap, focusMetric);
      const short = name.length > 14 ? `${name.slice(0, 12)}…` : name;
      return {
        key: `${name}-${i}`,
        label: `${i + 1}. ${short}`,
        fullLabel: name,
        value: raw,
        chartValue: raw == null ? null : raw,
        display: formatMetricDisplay(raw, metricMeta.format),
      };
    });
  }, [lines, snapshots, focusMetric, metricMeta]);

  const chartHeight = Math.min(520, Math.max(140, lines.length * 26 + 80));

  const chartTooltip = useCallback(
    ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
          }}
        >
          <Typography variant="caption" display="block" fontWeight={600}>
            {row.fullLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {metricMeta?.label}: {row.display}
          </Typography>
        </Box>
      );
    },
    [metricMeta]
  );

  const unknown = parsedSummary.unknown;

  return (
    <Box sx={sx}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        <strong>{parsedSummary.counties.length}</strong> recognized {plural}
        {lines.length !== parsedSummary.counties.length ? (
          <>
            {" "}
            · <strong>{lines.length}</strong> line{lines.length === 1 ? "" : "s"} in list
          </>
        ) : null}
        . Add provinces from the map or checklist below (or a saved collection where available); remove with the trash
        control. {GTG_PROVINCE_GOOGLE_ADS_NOTE}
      </Typography>
      {unknown.length > 0 ? (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1.5 }}>
          Unrecognized — fix or remove: <strong>{unknown.join(", ")}</strong>
        </Typography>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <FormControl size="small" sx={{ minWidth: 320 }} disabled={disabled}>
          <InputLabel id="gtg-province-focus-metric-label">Focus metric</InputLabel>
          <Select
            labelId="gtg-province-focus-metric-label"
            label="Focus metric"
            value={focusMetric}
            onChange={(e) => setFocusMetric(String(e.target.value))}
          >
            {focusMetricList.map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Sort list low → high by this metric (missing / unrecognized last)">
            <span>
              <IconButton
                size="small"
                aria-label="Sort ascending by focus metric"
                disabled={disabled || lines.length < 2}
                onClick={() => applySort(true)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Sort list high → low by this metric (missing / unrecognized last)">
            <span>
              <IconButton
                size="small"
                aria-label="Sort descending by focus metric"
                disabled={disabled || lines.length < 2}
                onClick={() => applySort(false)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {lines.length > 0 ? (
        <Box sx={{ width: "100%", height: chartHeight, maxWidth: "100%", mb: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                type="number"
                tick={{ fontSize: 11 }}
                stroke={theme.palette.text.secondary}
                domain={[0, "auto"]}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={88}
                tick={{ fontSize: 10 }}
                stroke={theme.palette.text.secondary}
              />
              <RechartsTooltip content={chartTooltip} />
              <Bar
                dataKey="chartValue"
                name={metricMeta?.label}
                fill={theme.palette.primary.main}
                radius={[0, 4, 4, 0]}
                isAnimationActive={lines.length <= 40}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {tc === GTG_TARGET_COUNTRY_ES ? (
          <>
            <strong>Spain:</strong> {SPAIN_HEALTH_STATS_CAPTION}
          </>
        ) : tc === GTG_TARGET_COUNTRY_TH ? (
          <>
            <strong>Thailand:</strong> {THAILAND_HEALTH_STATS_CAPTION}
          </>
        ) : null}{" "}
        {GTG_PROVINCE_GEO_VINTAGE_CAPTION}
      </Typography>

      {lines.length > 0 ? (
        <List
          dense
          disablePadding
          sx={{
            maxHeight: 280,
            overflow: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {lines.map((line, idx) => {
            const snap = line.canonical ? snapshots[line.canonical] : null;
            const num = metricNumeric(snap, focusMetric);
            const secondary = line.canonical
              ? formatMetricDisplay(num, metricMeta?.format ?? "decimal")
              : "— (not in province list)";
            const primary = line.canonical ? getGtgProvinceDisplayName(line.canonical) : line.raw;
            return (
              <ListItem
                key={`${line.raw}-${idx}`}
                secondaryAction={
                  <Tooltip title="Remove from list">
                    <span>
                      <IconButton
                        edge="end"
                        aria-label={`Remove ${primary}`}
                        size="small"
                        disabled={disabled}
                        onClick={() => removeAt(idx)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemText primary={primary} secondary={secondary} />
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No {plural} yet. Use the map or checklist below to add provinces.
        </Typography>
      )}
    </Box>
  );
};

export default GtgProvinceTargetingField;
